import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import { Users, Calendar, CreditCard, Clock, ArrowRight, TrendingUp, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { isToday, isTomorrow, addDays, startOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, format, subMonths, eachMonthOfInterval, isSameMonth } from "date-fns";
import { formatDate, formatTime } from "@/lib/date-format";
import DateInput from "@/components/date-input";
import type { Student } from "@contracts/student";
import type { Lesson } from "@contracts/lesson";

interface ScheduleRule {
  dayOfWeek: number;
  startTime: string;
  duration: number;
  recurrenceType: "weekly" | "biweekly";
  googleEventId?: string;
}

interface ScheduleException {
  originalDate: string;
  newDate?: string;
  newDuration?: number;
  cancelled?: boolean;
}

interface UpcomingEvent {
  studentId: string;
  studentName: string;
  scheduledDate: Date;
  duration: number;
  lessonType: string;
  unitRate: string;
  unitCurrency: string;
  isVirtual: boolean;
  lessonId?: string;
}

function generateUpcomingEvents(students: Student[], lessons: Lesson[], count: number): UpcomingEvent[] {
  const now = new Date();
  const rangeEnd = addDays(now, 14);
  const events: UpcomingEvent[] = [];

  const futureLessons = lessons
    .filter(l => new Date(l.scheduledDate) >= now)
    .map(l => {
      const student = students.find(s => s.id === l.studentId);
      return {
        studentId: l.studentId,
        studentName: student?.name || "Unknown",
        scheduledDate: new Date(l.scheduledDate),
        duration: l.duration,
        lessonType: l.lessonType,
        unitRate: l.unitRate,
        unitCurrency: l.unitCurrency,
        isVirtual: false,
        lessonId: l.id,
      };
    });
  events.push(...futureLessons);

  const existingKeys = new Set<string>();
  for (const l of lessons) {
    existingKeys.add(`${l.studentId}_${new Date(l.scheduledDate).getTime()}`);
  }

  const ws = startOfWeek(now, { weekStartsOn: 0 });

  for (const student of students) {
    const rules = (student.scheduleRules as ScheduleRule[]) || [];
    if (rules.length === 0) continue;

    const exceptions = ((student as any).scheduleExceptions as ScheduleException[]) || [];
    const exceptionMap = new Map<string, ScheduleException>();
    for (const exc of exceptions) {
      exceptionMap.set(exc.originalDate, exc);
    }

    for (let w = 0; w < 3; w++) {
      for (const rule of rules) {
        if (rule.recurrenceType === "biweekly" && w % 2 !== 0) continue;
        const dayDate = addDays(ws, rule.dayOfWeek + w * 7);

        const [h, m] = rule.startTime.split(":").map(Number);
        const eventDate = new Date(dayDate);
        eventDate.setHours(h, m, 0, 0);

        if (eventDate < now || eventDate > rangeEnd) continue;

        const key = `${student.id}_${eventDate.getTime()}`;
        if (existingKeys.has(key)) continue;

        const exception = exceptionMap.get(eventDate.toISOString());
        if (exception?.cancelled) continue;

        let finalDate = eventDate;
        let finalDuration = rule.duration || student.lessonDuration;

        if (exception) {
          if (exception.newDate) finalDate = new Date(exception.newDate);
          if (exception.newDuration) finalDuration = exception.newDuration;
        }

        if (finalDate < now || finalDate > rangeEnd) continue;

        events.push({
          studentId: student.id,
          studentName: student.name,
          scheduledDate: finalDate,
          duration: finalDuration,
          lessonType: student.lessonType,
          unitRate: student.unitRate,
          unitCurrency: student.unitCurrency,
          isVirtual: true,
        });
      }
    }
  }

  events.sort((a, b) => a.scheduledDate.getTime() - b.scheduledDate.getTime());
  return events.slice(0, count);
}

function groupByCurrency(lessons: Lesson[]): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const l of lessons) {
    const amount = parseFloat(l.unitRate) * l.duration / 60;
    totals[l.unitCurrency] = (totals[l.unitCurrency] || 0) + amount;
  }
  return totals;
}

type EarningsPeriod = "this-month" | "last-month" | "this-year" | "last-6-months" | "custom";

function EarningsSection({ lessons }: { lessons: Lesson[] }) {
  const [period, setPeriod] = useState<EarningsPeriod>("this-month");
  const [customFrom, setCustomFrom] = useState(() => format(startOfMonth(new Date()), "yyyy-MM-dd"));
  const [customTo, setCustomTo] = useState(() => format(endOfMonth(new Date()), "yyyy-MM-dd"));

  const { dateRange, label } = useMemo(() => {
    const now = new Date();
    switch (period) {
      case "this-month":
        return { dateRange: { start: startOfMonth(now), end: endOfMonth(now) }, label: format(now, "MMMM yyyy") };
      case "last-month": {
        const lm = subMonths(now, 1);
        return { dateRange: { start: startOfMonth(lm), end: endOfMonth(lm) }, label: format(lm, "MMMM yyyy") };
      }
      case "this-year":
        return { dateRange: { start: startOfYear(now), end: endOfYear(now) }, label: format(now, "yyyy") };
      case "last-6-months": {
        const sixAgo = subMonths(now, 5);
        return { dateRange: { start: startOfMonth(sixAgo), end: endOfMonth(now) }, label: `${format(sixAgo, "MMM yyyy")} – ${format(now, "MMM yyyy")}` };
      }
      case "custom":
        return { dateRange: { start: new Date(customFrom), end: new Date(customTo + "T23:59:59") }, label: `${customFrom} – ${customTo}` };
    }
  }, [period, customFrom, customTo]);

  const paidLessons = useMemo(() => {
    return lessons.filter(l => {
      if (l.paymentStatus !== "paid") return false;
      const d = new Date(l.scheduledDate);
      return d >= dateRange.start && d <= dateRange.end;
    });
  }, [lessons, dateRange]);

  const earnedByCurrency = useMemo(() => groupByCurrency(paidLessons), [paidLessons]);
  const earnedEntries = Object.entries(earnedByCurrency);

  const monthlyBreakdown = useMemo(() => {
    if (period === "this-month" || period === "last-month") return null;

    const months = eachMonthOfInterval({ start: dateRange.start, end: dateRange.end });
    const breakdown: { month: string; totals: Record<string, number>; lessonCount: number }[] = [];

    for (const m of months) {
      const monthLessons = paidLessons.filter(l => isSameMonth(new Date(l.scheduledDate), m));
      const totals = groupByCurrency(monthLessons);
      breakdown.push({
        month: format(m, "MMM yyyy"),
        totals,
        lessonCount: monthLessons.length,
      });
    }
    return breakdown;
  }, [paidLessons, dateRange, period]);

  const maxMonthlyAmount = useMemo(() => {
    if (!monthlyBreakdown) return 0;
    let max = 0;
    for (const mb of monthlyBreakdown) {
      const total = Object.values(mb.totals).reduce((a, b) => a + b, 0);
      if (total > max) max = total;
    }
    return max;
  }, [monthlyBreakdown]);

  return (
    <Card className="p-5" data-testid="card-earnings">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
          <h2 className="font-semibold">Earnings</h2>
        </div>
        <Select value={period} onValueChange={(v) => setPeriod(v as EarningsPeriod)}>
          <SelectTrigger className="w-[160px]" data-testid="select-earnings-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="this-month">This Month</SelectItem>
            <SelectItem value="last-month">Last Month</SelectItem>
            <SelectItem value="this-year">This Year</SelectItem>
            <SelectItem value="last-6-months">Last 6 Months</SelectItem>
            <SelectItem value="custom">Custom Range</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {period === "custom" && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">From</label>
            <DateInput value={customFrom} onChange={setCustomFrom} data-testid="input-earnings-from" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">To</label>
            <DateInput value={customTo} onChange={setCustomTo} data-testid="input-earnings-to" />
          </div>
        </div>
      )}

      <div className="mb-4">
        <p className="text-xs text-muted-foreground mb-1">{label}</p>
        <div className="flex items-baseline gap-3 flex-wrap">
          {earnedEntries.length === 0 ? (
            <p className="text-2xl font-bold tracking-tight text-muted-foreground">0</p>
          ) : (
            earnedEntries.map(([currency, amount]) => (
              <div key={currency} className="flex items-baseline gap-1">
                <p className="text-2xl font-bold tracking-tight text-green-600 dark:text-green-400">
                  {amount.toFixed(2)}
                </p>
                <span className="text-sm text-muted-foreground">{currency}</span>
              </div>
            ))
          )}
          <span className="text-xs text-muted-foreground">
            ({paidLessons.length} lesson{paidLessons.length !== 1 ? "s" : ""})
          </span>
        </div>
      </div>

      {monthlyBreakdown && monthlyBreakdown.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground font-medium">Monthly breakdown</p>
          <div className="space-y-1.5">
            {monthlyBreakdown.map((mb) => {
              const total = Object.values(mb.totals).reduce((a, b) => a + b, 0);
              const barWidth = maxMonthlyAmount > 0 ? (total / maxMonthlyAmount) * 100 : 0;
              return (
                <div key={mb.month} className="group" data-testid={`earnings-month-${mb.month}`}>
                  <div className="flex items-center justify-between text-xs mb-0.5">
                    <span className="text-muted-foreground w-20 flex-shrink-0">{mb.month}</span>
                    <span className="font-medium">
                      {Object.entries(mb.totals).map(([c, a]) => `${a.toFixed(0)} ${c}`).join(" + ") || "0"}
                    </span>
                  </div>
                  <div className="h-4 bg-muted/30 rounded overflow-hidden">
                    <div
                      className="h-full bg-green-500/60 dark:bg-green-500/40 rounded transition-all duration-300"
                      style={{ width: `${Math.max(barWidth, total > 0 ? 2 : 0)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

    </Card>
  );
}

export default function DashboardPage() {
  const { data: students = [], isLoading: studentsLoading } = useQuery<Student[]>({
    queryKey: ["/api/students"],
  });

  const { data: allLessons = [], isLoading: lessonsLoading } = useQuery<Lesson[]>({
    queryKey: ["/api/lessons"],
  });

  const isLoading = studentsLoading || lessonsLoading;

  const pendingLessons = allLessons.filter(l => l.paymentStatus === "pending");
  const paidLessons = allLessons.filter(l => l.paymentStatus === "paid");

  const upcomingEvents = useMemo(
    () => generateUpcomingEvents(students, allLessons, 10),
    [students, allLessons]
  );

  const todayCount = useMemo(() => {
    return upcomingEvents.filter(e => isToday(e.scheduledDate)).length;
  }, [upcomingEvents]);

  const tomorrowCount = useMemo(() => {
    return upcomingEvents.filter(e => isTomorrow(e.scheduledDate)).length;
  }, [upcomingEvents]);

  const pendingByCurrency = useMemo(() => groupByCurrency(pendingLessons), [pendingLessons]);
  const pendingCurrencyEntries = Object.entries(pendingByCurrency);
  const pendingByStudent = useMemo(() => {
    const map = new Map<string, { student: Student; unpaidCount: number; totalDue: number }>();
    for (const lesson of pendingLessons) {
      const student = students.find((s) => s.id === lesson.studentId);
      if (!student) continue;
      const amount = parseFloat(lesson.unitRate) * lesson.duration / 60;
      const current = map.get(student.id);
      if (current) {
        current.unpaidCount += 1;
        current.totalDue += amount;
      } else {
        map.set(student.id, { student, unpaidCount: 1, totalDue: amount });
      }
    }
    return [...map.values()].sort((a, b) => b.unpaidCount - a.unpaidCount || a.student.name.localeCompare(b.student.name));
  }, [pendingLessons, students]);

  const earnedByCurrency = useMemo(() => groupByCurrency(paidLessons), [paidLessons]);
  const earnedCurrencyEntries = Object.entries(earnedByCurrency);

  function formatLessonDate(date: Date) {
    const time = formatTime(date);
    if (isToday(date)) return `Today, ${time}`;
    if (isTomorrow(date)) return `Tomorrow, ${time}`;
    return `${formatDate(date)}, ${time}`;
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-28" />)}
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-5 sm:space-y-6 max-w-7xl mx-auto" data-testid="page-dashboard">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight" data-testid="text-page-title">Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-1">Overview of your teaching activity</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
        <Card className="p-5" data-testid="card-stat-total-students">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Total Students</p>
              <p className="text-2xl font-bold tracking-tight">{students.length}</p>
            </div>
            <div className="w-10 h-10 rounded-md bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>

        <Card className="p-5" data-testid="card-stat-today-lessons">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Today's Lessons</p>
              <p className="text-2xl font-bold tracking-tight">{todayCount}</p>
            </div>
            <div className="w-10 h-10 rounded-md bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>

        <Card className="p-5" data-testid="card-stat-upcoming-lessons">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Tomorrow's Lessons</p>
              <p className="text-2xl font-bold tracking-tight">{tomorrowCount}</p>
            </div>
            <div className="w-10 h-10 rounded-md bg-green-50 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>

        <Card className="p-5" data-testid="card-stat-pending-payments">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Pending Payments</p>
              <p className="text-2xl font-bold tracking-tight">{pendingLessons.length}</p>
            </div>
            <div className="w-10 h-10 rounded-md bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
        </Card>

        <Card className="p-5" data-testid="card-stat-total-due">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Total Due</p>
              {pendingCurrencyEntries.length === 0 ? (
                <p className="text-2xl font-bold tracking-tight">0</p>
              ) : (
                <div className="space-y-0.5">
                  {pendingCurrencyEntries.map(([currency, amount]) => (
                    <p key={currency} className="text-lg font-bold tracking-tight">
                      {amount.toFixed(0)} {currency}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <div className="w-10 h-10 rounded-md bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </Card>

        <Card className="p-5" data-testid="card-stat-total-earned">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Total Earned</p>
              {earnedCurrencyEntries.length === 0 ? (
                <p className="text-2xl font-bold tracking-tight">0</p>
              ) : (
                <div className="space-y-0.5">
                  {earnedCurrencyEntries.map(([currency, amount]) => (
                    <p key={currency} className="text-lg font-bold tracking-tight text-green-600 dark:text-green-400">
                      {amount.toFixed(0)} {currency}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <div className="w-10 h-10 rounded-md bg-green-50 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5" data-testid="card-upcoming-lessons">
          <div className="flex items-center justify-between gap-2 mb-4">
            <h2 className="font-semibold">Upcoming Lessons</h2>
            <Button variant="ghost" size="sm" asChild>
              <Link href="/calendar">
                View All <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Link>
            </Button>
          </div>
          {upcomingEvents.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">
              <Calendar className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>No upcoming lessons scheduled</p>
              <Button variant="outline" size="sm" className="mt-3" asChild>
                <Link href="/students">Add a Student</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {upcomingEvents.map((event, idx) => (
                <div key={event.lessonId || `virtual-${idx}`} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30" data-testid={`lesson-upcoming-${idx}`}>
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs flex-shrink-0">
                    {event.studentName.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{event.studentName}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatLessonDate(event.scheduledDate)} · {event.duration}min
                    </p>
                  </div>
                  <Badge variant={event.lessonType === "online" ? "default" : "secondary"}>
                    {event.lessonType}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5" data-testid="card-pending-payments">
          <div className="flex items-center justify-between gap-2 mb-4">
            <h2 className="font-semibold">Pending payments</h2>
            <Button variant="ghost" size="sm" asChild>
              <Link href="/students">
                Manage <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Link>
            </Button>
          </div>
          {pendingByStudent.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>No pending payments</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pendingByStudent.map(({ student, unpaidCount, totalDue }) => {
                return (
                  <Link key={student.id} href={`/students/${student.id}`}>
                    <div className="flex items-center gap-3 p-3 rounded-lg hover-elevate cursor-pointer" data-testid={`student-card-${student.id}`}>
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs flex-shrink-0">
                        {student.name.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{student.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {unpaidCount} unpaid lesson{unpaidCount !== 1 ? "s" : ""}
                        </p>
                      </div>
                      <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                        {totalDue.toFixed(0)} {student.unitCurrency}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <EarningsSection lessons={allLessons} />
    </div>
  );
}
